import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Chip, Grid, IconButton, Stack } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { styled, alpha } from '@mui/material/styles'
import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'
import { ChipProps } from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import React from 'react'
import Button from '@mui/material/Button'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import Menu, { MenuProps } from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useIsWebView } from '@/hooks/useSizeBreakpoints'

export type ConsultationHeaderProps = {
  isDoctor: boolean
  consultation: Consultation
  actions: { label: string; actionHandler: () => void; fontColor: string }[]
}

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}))

export function ConsultationHeader(props: ConsultationHeaderProps) {
  const { isDoctor, consultation, actions } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const getColor = (status: string) => {
    let color: ChipProps['color']
    switch (status) {
      case 'scheduled':
        color = 'primary'
        break
      case 'accepted':
        color = 'success'
        break
      case 'canceled':
      case 'refused':
        color = 'error'
        break
      default:
        color = 'info'
        break
    }
    return color
  }
  const isWebView = useIsWebView()
  const isSameDay = isWebView ? (dayjs(consultation.startDate).isSame(dayjs(consultation.endDate), 'day')) : false
  const bFormat = (isWebView && consultation.endDate) ? (isSameDay ? 'DD/MM/YY' : 'DD/MM') : 'DD/MM/YYYY'
  const eFormat = isWebView ? 'DD/MM/YY' : 'DD/MM/YYYY'
  const hFormat = isSameDay || !isWebView ? 'HH[h]mm' : 'HH[h]'

  return (
    <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={3}>
              <IconButton aria-label="back to consultations" id="previous-button" data-testid="previous-button" onClick={()=>{navigate("/consultations")}} size="small" sx={{ mt: '10px' }}>
                <ArrowBackIcon color="primary" fontSize="small" />
              </IconButton>
      <Typography variant="h2" component="div" noWrap sx={{ fontSize: '24px', fontWeight: 700, mt: '16px', mb: '10px' }}>
        {t('page.consultation.title', { patient: _.trim(`${consultation.patient?.userInfo?.surname || ''} ${consultation.patient?.userInfo?.name || ''}`) })}
      </Typography>
      </Stack>
      <Stack direction= 'row' alignItems="center" spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-evenly" alignItems="end" spacing={1} /* sx={{ flexWrap: 'wrap' }}  */pb={1} pt={1}>
          <Chip
            color="secondary"
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>
                  {dayjs(consultation.startDate).format(bFormat)} - <b>{dayjs(consultation.startDate).format(hFormat)}</b>
                </span>
                {consultation.endDate && (
                  <>
                    <ArrowForwardIcon fontSize="small" />
                    {isSameDay ? 
                    <span>
                      <b>{dayjs(consultation.endDate).format('HH[h]mm')}</b>
                    </span>
                    :
                    <span>
                      {dayjs(consultation.endDate).format(eFormat)} - <b>{dayjs(consultation.endDate).format(hFormat)}</b>
                    </span>}
                  </>
                )}
              </Stack>
            }
            variant="filled"
            sx={{ mb: { xs: 2, sm: 0 } }}
          />
          <Chip
            color={getColor(consultation.status)}
            style={{ fontWeight: 'bold' }}
            label={t(`common:model.consultation.status.${consultation.status}.label`, { context: isDoctor ? 'doctor' : 'pds' }).toUpperCase()}
          />
        </Stack>
        <Stack direction="row" sx={{ justifyContent: { xs: 'space-evenly', sm: 'flex-end' } }} alignItems="flex-end" spacing={5}>
          <div>
            <Button
              disabled={consultation.status === ConsultationStatus.Canceled}
              aria-controls={open ? 'demo-customized-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              variant="contained"
              color="info"
              disableElevation
              onClick={handleClick}
              endIcon={<KeyboardArrowDownIcon />}
            >
              {t('common:button.action.label')}
            </Button>
            <StyledMenu
              id="demo-customized-menu"
              MenuListProps={{
                'aria-labelledby': 'demo-customized-button',
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              {actions.map((action, index) => (
                <MenuItem onClick={action.actionHandler} key={`menu-item-${index}`}>
                  <Typography fontWeight="bold" color={action.fontColor}>
                    {action.label}
                  </Typography>
                </MenuItem>
              ))}
            </StyledMenu>
          </div>
        </Stack>
      </Stack>
    </Grid>
  )
}

export default ConsultationHeader
